<template>
  <!--
    Studio · Reading Sessions — the per-game authoring surface.

    Layout:
      [ Tarot Reading ] [ + Add Skill ]              ← skill tabs
      [ Single Card Focus ▾ ]   [ 50 ✦ ]  ⤿ Archive  ← game bar (variant + price, persists)
      [ Card Deck | Start | Card Selection | Reading ]← journey sub-tabs
      <only the active section>

    The game bar picks the spread (which re-shapes Card Selection + Reading).
    The beat fields author plain-language $moment_intent per journey moment;
    the reader's cross-cutting voice ($reader_slant) lives in the Profile tab.
  -->
  <div class="studio-panel rsp-wide">
    <!-- Per-skill tab strip -->
    <div class="skill-tabs">
      <button
        v-for="skill in skills"
        :key="skill.skill_key"
        class="skill-tab"
        :class="{ active: activeSkill === skill.skill_key }"
        @click="selectSkill(skill.skill_key)"
      >
        {{ skill.display_name }}
      </button>
      <button class="skill-tab add-skill" :disabled="true"
      title="Coming soon - activate a new skill via Skill Builder for now">
        + Add Skill
      </button>
    </div>

    <div v-if="loadingTree" class="loading">Loading {{ activeSkill }}…</div>

    <div v-else-if="tree" class="skill-body">
      <div v-if="!tree.variants.length" class="empty-hint">
        No active variants yet. Add one via the Skill Builder.
      </div>

      <template v-else>
        <!-- ── Game bar: spread picker + price + archive (persists across tabs) ── -->
        <header v-if="activeVariant" class="game-bar">
          <SkillSettingsInline
            :variant="activeVariantWithSkill"
            :confirm-switch="true"
            @pick-requested="onPickRequested"
            @snapshots="openSnapshots"
          />
          <div class="game-bar-right">
            <div class="variant-pricing">
              <!-- Plain words for a first-time Mystic (e2e round 2, S41). -->
              <label class="price-label" data-testid="price-label" :for="`price-${activeVariant.id}`">Price per reading</label>
              <input
                :id="`price-${activeVariant.id}`"
                type="number" :min="currentFloor" step="5"
                class="price-input"
                :class="{ 'price-input-error': !!priceError }"
                v-model.number="activeVariant.price_carats_draft"
                @blur="savePricing(activeVariant)"
                :aria-label="`Session price (min ${currentFloor} carats)`"
              />
              <span class="emerald-symbol">✦</span>
              <span v-if="savingVariantId === activeVariant.id" class="price-status">Saving…</span>
              <span v-else-if="savedVariantId === activeVariant.id" class="price-status saved">Saved</span>
            </div>
            <!-- Publish Live — opens the publish modal (what changed + preview +
                 publish). The dot signals unpublished changes. -->
            <div class="publish-wrap">
            <button
              type="button" class="publish-btn"
              :class="{ 'has-changes': hasUnpublishedChanges }"
              :disabled="!hasUnpublishedChanges"
              :title="hasUnpublishedChanges ? 'Review your draft changes and publish them live' : 'Nothing to publish - draft matches live'"
              @click="showPublishModal = true"
            >
              <span v-if="hasUnpublishedChanges" class="publish-dot" aria-hidden="true"></span>
              Publish Live
            </button>
            <span class="publish-hint" data-testid="publish-hint">Make it live so seekers can book it</span>
            </div>
          </div>
        </header>
        <p v-if="priceError" class="price-error" role="alert">{{ priceError }}</p>

        <!-- ── Journey sub-tabs ── -->
        <div class="sub-tabs">
          <button
            v-for="t in subTabs"
            :key="t.key"
            class="sub-tab"
            :class="{ active: activeSub === t.key }"
            @click="activeSub = t.key"
          >
            <span class="sub-tab-label">{{ t.label }}</span>
            <span v-if="variantSwitching" class="sub-tab-spinner" aria-label="Loading new variant" />
          </button>
        </div>

        <div
          class="sub-body"
          :class="{ 'sub-body-split': showSim && !simStacked }"
        >
         <div class="sub-center" :class="{ 'sub-center-cs': activeSub === 'card_selection' }">
          <!-- ── Card Deck: the deck this game draws from ── -->
          <div v-if="activeSub === 'card_deck'">
            <div class="deck-picker-section">
              <SkillsEditorView :hide-skill-tabs="true" :variant-id="activeVariantId" />
            </div>
          </div>

          <!-- ── Start: Grounding + Interview ── -->
          <div v-else-if="activeSub === 'start'">
            <div v-for="m in START_BEATS" :key="m.slug" class="beat-field">
              <div
                class="beat-card"
                :class="{
                  'beat-card-active': beatIsOpen(m.slug) || (simBeat && simBeat.slug === m.slug),
                  'beat-card-expanded': isExpanded(m.slug),
                }"
              >
                <div class="beat-card-header" @click="toggleBeat(m)">
                  <span class="beat-head-left">
                    <span v-if="activeBeatsCount > 1" class="beat-chevron" :class="{ open: isExpanded(m.slug) }">▸</span>
                    <input
                      v-if="m.editableLabel && isExpanded(m.slug)"
                      class="beat-label-edit"
                      :value="m.label"
                      maxlength="60"
                      title="Rename this position - shown in the reveal (“For the … card, you selected …”)"
                      aria-label="Position label"
                      @click.stop
                      @keydown.enter.prevent="$event.target.blur()"
                      @blur="savePositionLabel(m.role, $event)"
                    />
                    <span v-else class="beat-label-text">{{ m.label }}</span>
                  </span>
                </div>
                <div v-show="isExpanded(m.slug)" class="beat-card-body">
                  <div v-if="m.slug !== 'image'" class="beat-limit" :title="isWordBeat(m.slug) ? 'How long this beat runs (words)' : 'Max response length for this beat'">
                    <input
                      type="number" class="beat-limit-input"
                      :min="beatLimitMin(m.slug)"
                      :max="beatLimitMax(m.slug)"
                      :step="beatLimitStep(m.slug)"
                      v-model.number="readingStyle.limits[m.slug]"
                      @blur="saveLimit(m.slug)"
                      :aria-label="isWordBeat(m.slug) ? 'Length in words' : 'Response character limit'"
                    />
                    <span class="beat-limit-suffix">{{ isWordBeat(m.slug) ? 'Words' : 'Characters' }}</span>
                  </div>
                  <!-- THE READING also shapes the seeker-facing summary TITLE. -->
                  <div v-if="m.slug === 'reading'" class="reading-title-field">
                    <span class="reading-title-label">
                      Title direction
                      <FieldHelpIcon
                        field="moment:reading_title"
                        label="Reading title"
                        panel="reading-session"
                        :value="readingStyle.moments.reading_title || ''"
                        :meta="{ skillKey: activeSkill }"
                      />
                    </span>
                    <input
                      type="text" class="reading-title-input"
                      :value="readingStyle.moments.reading_title || ''"
                      placeholder="e.g., short, present tense, evocative"
                      @blur="saveTitleDirection($event)"
                    />
                  </div>
                  <div v-if="m.slug === 'interview'" class="beat-pace">
                    <span class="beat-pace-label">Pace</span>
                    <input
                      type="range" min="0" max="2" step="1"
                      class="beat-pace-slider"
                      :value="paceIndex"
                      @input="onPaceSlide($event.target.value)"
                      aria-label="Interview pace"
                    />
                    <div class="beat-pace-ticks">
                      <span
                        v-for="opt in PACE_OPTIONS" :key="opt.value"
                        class="beat-pace-tick"
                        :class="{ active: interviewPace === opt.value }"
                      >{{ opt.label }}</span>
                    </div>
                  </div>
                  <!-- Image Direction: brand collapses to a tiny line. -->
                  <details v-if="m.showsBrand" class="brand-ref-mini">
                    <summary>✦ Your visual brand is applied</summary>
                    <p v-if="brandReference" class="brand-ref-text">{{ brandReference }}</p>
                    <p v-else class="brand-ref-empty">
                      No visual brand yet - author it in <b>Voice → Visual Voice</b>.
                    </p>
                  </details>
                  <span v-if="readingStyle.moments[m.slug]" class="beat-card-text">{{ readingStyle.moments[m.slug] }}</span>
                  <span v-else class="beat-card-empty">{{ m.placeholder }}</span>
                  <div class="beat-card-actions">
                    <button type="button" class="beat-btn beat-btn-refine" @click="openBeat(m)">
                      ✨ {{ readingStyle.moments[m.slug] ? 'Refine' : 'Write' }}
                    </button>
                    <button
                      type="button" class="beat-btn beat-btn-history"
                      :title="`Version history for ${m.label}`"
                      :aria-label="`Version history for ${m.label}`"
                      @click="openBeatHistory(m)"
                    >↻</button>
                    <span v-if="momentSaved === m.slug" class="beat-card-saved">Saved</span>
                    <button v-if="m.slug !== 'image' && m.slug !== 'reading'" type="button" class="beat-btn beat-btn-sim" @click="simulateBeat(m)">
                      {{ simBeat && simBeat.slug === m.slug ? '↻ Re-Simulate' : '▶ Simulate' }}
                    </button>
                  </div>
                  <!-- The illustration is previewed in context via THE READING's
                       Simulate (the modal's illustration slot). -->
                </div>
              </div>
            </div>
          </div>

          <!-- ── Card Selection: meaning toggle (fixed) · scrolling beats ·
               pinned chat-window preview. The pane is height-bounded so ONLY the
               beats scroll; the preview stays put above the menu bar. ── -->
          <div v-else-if="activeSub === 'card_selection'" class="cs-pane">
            <!-- Meaning source — what the reveal interprets from. The info
                 dot explains both options on hover / tap. -->
            <div class="meaning-source">
              <div class="meaning-source-toggle" role="group" aria-label="Card meaning source">
                <button
                  type="button" class="ms-opt" :class="{ active: meaningSource === 'symbolic' }"
                  @click="setMeaningSource('symbolic')"
                >✦ Mystic’s Symbolism</button>
                <button
                  type="button" class="ms-opt" :class="{ active: meaningSource === 'generic' }"
                  @click="setMeaningSource('generic')"
                >Tarot’s Symbolism</button>
              </div>
              <span class="meaning-source-info" :class="{ open: meaningInfoOpen }">
                <button
                  type="button" class="ms-info-btn"
                  aria-label="What do these mean?"
                  @click="meaningInfoOpen = !meaningInfoOpen"
                  @blur="meaningInfoOpen = false"
                >ℹ</button>
                <span class="ms-info-pop" role="tooltip">
                  <span class="ms-info-row">
                    <b>✦ Mystic’s Symbolism</b> - your deck’s authored card names
                    + meanings (View in Card Deck).
                  </span>
                  <span class="ms-info-row">
                    <b>Tarot’s Symbolism</b> - the canonical tarot card names
                    + meanings.
                  </span>
                </span>
              </span>
            </div>

            <p class="cs-teaser-note">
              <b>Card Teaser</b> - the short line shown as each card turns over (a few words).
            </p>

            <!-- Only this list scrolls (height-bounded by .cs-pane). The reveal
                 is driven by the card's symbolic meaning (toggle above) + word
                 count + Profile voice + the Mysticism dial. -->
            <div class="cs-scroll">
            <div v-for="m in cardRevealBeats" :key="m.slug" class="beat-field">
              <div
                class="beat-card"
                :class="{
                  'beat-card-active': simBeat && simBeat.slug === m.slug,
                  'beat-card-expanded': isExpanded(m.slug),
                }"
              >
                <div class="beat-card-header" @click="toggleBeat(m)">
                  <span class="beat-head-left">
                    <span v-if="activeBeatsCount > 1" class="beat-chevron" :class="{ open: isExpanded(m.slug) }">▸</span>
                    <input
                      v-if="m.editableLabel && isExpanded(m.slug)"
                      class="beat-label-edit"
                      :value="m.label"
                      maxlength="60"
                      title="Rename this position - shown in the reveal (“For the … card, you selected …”)"
                      aria-label="Position label"
                      @click.stop
                      @keydown.enter.prevent="$event.target.blur()"
                      @blur="savePositionLabel(m.role, $event)"
                    />
                    <span v-else class="beat-label-text">{{ m.label }}</span>
                    <span v-if="m.editableLabel && isExpanded(m.slug)" class="beat-label-help" @click.stop>
                      <FieldHelpIcon
                        :field="`position:${m.role}`"
                        :label="`${m.label} position name`"
                        panel="reading-session"
                        :value="positionLabels[m.role] || m.label"
                        :meta="{ skillKey: activeSkill, role: m.role }"
                      />
                    </span>
                  </span>
                </div>
                <div v-show="isExpanded(m.slug)" class="beat-card-body">
                  <div class="cs-beat-controls">
                    <label class="cs-words" title="How many words show when this card turns">
                      <select
                        class="cs-words-select"
                        v-model.number="readingStyle.limits[m.slug]"
                        @change="saveLimit(m.slug)"
                        aria-label="Reveal word count (1 to 10)"
                      >
                        <option v-for="n in 10" :key="n" :value="n">{{ n }}</option>
                      </select>
                      <span class="cs-words-label">words</span>
                    </label>
                    <label class="cs-stream" title="Where this card's reveal lands in the chat. 'After the summary' holds it back so it reads as the closing counsel; 'Not in chat' draws it privately - it still appears in the reading.">
                      <span class="cs-stream-label">Reveal</span>
                      <select
                        class="cs-stream-select"
                        :value="getStreamMode(m.role)"
                        @change="saveStreamMode(m.role, $event.target.value)"
                        :aria-label="`Where the ${m.label} card is revealed`"
                      >
                        <option value="on_draw">As it's drawn</option>
                        <option value="after_summary">After the summary</option>
                        <option value="private">Not in chat</option>
                      </select>
                    </label>
                    <button type="button" class="beat-btn beat-btn-sim" @click="simulateBeat(m)">
                      {{ simBeat && simBeat.slug === m.slug ? '↻ Re-Simulate' : '▶ Simulate' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            </div>
            <!-- Pinned preview — sits below the scrolling beats, always visible. -->
            <BeatPreviewRail
              v-if="simBeat"
              class="cs-sim"
              :stacked="true"
              v-bind="simRailProps"
              @close="closeSim"
            />
          </div>

          <!-- ── Reading: the synthesis (stack analysis) + the image ── -->
          <div v-else-if="activeSub === 'reading'">
            <!-- One Simulate drives BOTH beats — the modal shows the woven
                 reading AND its illustration together. -->
            <div class="reading-sim-bar">
              <button type="button" class="reading-sim-btn" @click="simulateReading">
                ▶ Simulate Reading
              </button>
            </div>
            <!-- Per-card SUMMARY beats — one per spread position (hidden for a
                 1-card draw). Each steers how that card reads in the reading
                 window (the seeker's per-card summary), distinct from its short
                 Card Teaser in Card Selection. Authored like any other beat. -->
            <p v-if="readingSummaryBeats.length" class="beat-note">
              <b>Card Summary</b> - the fuller read shown beside each card in the reading.
            </p>
            <div v-for="m in readingSummaryBeats" :key="m.slug" class="beat-field">
              <div
                class="beat-card"
                :class="{ 'beat-card-active': beatIsOpen(m.slug), 'beat-card-expanded': isExpanded(m.slug) }"
              >
                <div class="beat-card-header" @click="toggleBeat(m)">
                  <span class="beat-head-left">
                    <span class="beat-chevron" :class="{ open: isExpanded(m.slug) }">▸</span>
                    <span class="beat-label-text">{{ m.label }}</span>
                  </span>
                </div>
                <div v-show="isExpanded(m.slug)" class="beat-card-body">
                  <span v-if="readingStyle.moments[m.slug]" class="beat-card-text">{{ readingStyle.moments[m.slug] }}</span>
                  <span v-else class="beat-card-empty">{{ m.placeholder }}</span>
                  <div class="beat-card-actions">
                    <button type="button" class="beat-btn beat-btn-refine" @click="openBeat(m)">
                      ✨ {{ readingStyle.moments[m.slug] ? 'Refine' : 'Write' }}
                    </button>
                    <button
                      type="button" class="beat-btn beat-btn-history"
                      :title="`Version history for the ${m.label} summary`"
                      :aria-label="`Version history for the ${m.label} summary`"
                      @click="openBeatHistory(m)"
                    >↻</button>
                    <span v-if="momentSaved === m.slug" class="beat-card-saved">Saved</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-for="m in READING_BEATS" :key="m.slug" class="beat-field">
              <p v-if="m.note" class="beat-note beat-note-inline" v-html="m.note"></p>
              <div
                class="beat-card"
                :class="{
                  'beat-card-active': beatIsOpen(m.slug) || (simBeat && simBeat.slug === m.slug),
                  'beat-card-expanded': isExpanded(m.slug),
                }"
              >
                <div class="beat-card-header" @click="toggleBeat(m)">
                  <span class="beat-head-left">
                    <span v-if="activeBeatsCount > 1" class="beat-chevron" :class="{ open: isExpanded(m.slug) }">▸</span>
                    <input
                      v-if="m.editableLabel && isExpanded(m.slug)"
                      class="beat-label-edit"
                      :value="m.label"
                      maxlength="60"
                      title="Rename this position - shown in the reveal (“For the … card, you selected …”)"
                      aria-label="Position label"
                      @click.stop
                      @keydown.enter.prevent="$event.target.blur()"
                      @blur="savePositionLabel(m.role, $event)"
                    />
                    <span v-else class="beat-label-text">{{ m.label }}</span>
                  </span>
                </div>
                <div v-show="isExpanded(m.slug)" class="beat-card-body">
                  <div v-if="m.slug !== 'image'" class="beat-limit" :title="isWordBeat(m.slug) ? 'How long this beat runs (words)' : 'Max response length for this beat'">
                    <input
                      type="number" class="beat-limit-input"
                      :min="beatLimitMin(m.slug)"
                      :max="beatLimitMax(m.slug)"
                      :step="beatLimitStep(m.slug)"
                      v-model.number="readingStyle.limits[m.slug]"
                      @blur="saveLimit(m.slug)"
                      :aria-label="isWordBeat(m.slug) ? 'Length in words' : 'Response character limit'"
                    />
                    <span class="beat-limit-suffix">{{ isWordBeat(m.slug) ? 'Words' : 'Characters' }}</span>
                  </div>
                  <!-- THE READING also shapes the seeker-facing summary TITLE. -->
                  <div v-if="m.slug === 'reading'" class="reading-title-field">
                    <span class="reading-title-label">
                      Title direction
                      <FieldHelpIcon
                        field="moment:reading_title"
                        label="Reading title"
                        panel="reading-session"
                        :value="readingStyle.moments.reading_title || ''"
                        :meta="{ skillKey: activeSkill }"
                      />
                    </span>
                    <input
                      type="text" class="reading-title-input"
                      :value="readingStyle.moments.reading_title || ''"
                      placeholder="e.g., short, present tense, evocative"
                      @blur="saveTitleDirection($event)"
                    />
                  </div>
                  <div v-if="m.slug === 'interview'" class="beat-pace">
                    <span class="beat-pace-label">Pace</span>
                    <input
                      type="range" min="0" max="2" step="1"
                      class="beat-pace-slider"
                      :value="paceIndex"
                      @input="onPaceSlide($event.target.value)"
                      aria-label="Interview pace"
                    />
                    <div class="beat-pace-ticks">
                      <span
                        v-for="opt in PACE_OPTIONS" :key="opt.value"
                        class="beat-pace-tick"
                        :class="{ active: interviewPace === opt.value }"
                      >{{ opt.label }}</span>
                    </div>
                  </div>
                  <!-- Image Direction: brand collapses to a tiny line. -->
                  <details v-if="m.showsBrand" class="brand-ref-mini">
                    <summary>✦ Your visual brand is applied</summary>
                    <p v-if="brandReference" class="brand-ref-text">{{ brandReference }}</p>
                    <p v-else class="brand-ref-empty">
                      No visual brand yet - author it in <b>Voice → Visual Voice</b>.
                    </p>
                  </details>
                  <span v-if="readingStyle.moments[m.slug]" class="beat-card-text">{{ readingStyle.moments[m.slug] }}</span>
                  <span v-else class="beat-card-empty">{{ m.placeholder }}</span>
                  <div class="beat-card-actions">
                    <button type="button" class="beat-btn beat-btn-refine" @click="openBeat(m)">
                      ✨ {{ readingStyle.moments[m.slug] ? 'Refine' : 'Write' }}
                    </button>
                    <button
                      type="button" class="beat-btn beat-btn-history"
                      :title="`Version history for ${m.label}`"
                      :aria-label="`Version history for ${m.label}`"
                      @click="openBeatHistory(m)"
                    >↻</button>
                    <span v-if="momentSaved === m.slug" class="beat-card-saved">Saved</span>
                    <button v-if="m.slug !== 'image' && m.slug !== 'reading'" type="button" class="beat-btn beat-btn-sim" @click="simulateBeat(m)">
                      {{ simBeat && simBeat.slug === m.slug ? '↻ Re-Simulate' : '▶ Simulate' }}
                    </button>
                  </div>
                  <!-- The illustration is previewed in context via THE READING's
                       Simulate (the modal's illustration slot). -->
                </div>
              </div>
            </div>
          </div>
         </div>

          <!-- ── Simulator rail (Start / Reading only — Card Selection renders its
               own pinned preview inside the pane above). ── -->
          <BeatPreviewRail
            v-if="simBeat && !simStacked"
            v-bind="simRailProps"
            class="sub-rail"
            :stacked="false"
            @close="closeSim"
          />
        </div>
      </template>
    </div>

    <p v-if="error" class="error-message">{{ error }}</p>

    <!-- Publish modal — review the draft-vs-live diff, preview, then publish. -->
    <PublishModal
      v-if="showPublishModal"
      :skill-key="activeSkill"
      @close="showPublishModal = false"
      @published="hasUnpublishedChanges = false"
      @preview="simulateReading"
    />

    <!-- Full-reading simulator modal (THE READING beat). -->
    <ReadingModal
      v-if="showReadingModal"
      :skill-key="activeSkill"
      :moment-intent="readingStyle.moments.reading || ''"
      :image-direction="readingStyle.moments.image || ''"
      :reader-slant="readingStyle.style"
      :max-words="readingStyle.limits.reading || 0"
      :meaning-source="meaningSource"
      :run-nonce="readingModalNonce"
      @close="showReadingModal = false"
    />

    <!-- Snapshots modal — ONE timeline: published versions + auto-saved
         spread-switch backups, each tagged by how it was made. Restore loads it
         back into the draft. (Game History + Archive were merged — same idea.) -->
    <div v-if="showSnapshotsModal" class="archive-modal-overlay" @click.self="showSnapshotsModal = false">
      <div class="archive-modal" role="dialog" aria-label="Snapshots">
        <header class="archive-modal-head">
          <h4 class="archive-title">Snapshots</h4>
          <button type="button" class="archive-modal-close" aria-label="Close" @click="showSnapshotsModal = false">×</button>
        </header>
        <p class="archive-sub">
          Every published version and auto-saved spread switch - restore any to load it back into your draft.
        </p>
        <div v-if="snapshotsLoading" class="archive-empty">Loading…</div>
        <div v-else-if="!snapshots.length" class="archive-empty">
          No snapshots yet. Publishing or switching spreads saves one here automatically.
        </div>
        <ul v-else class="archive-list">
          <li v-for="snap in snapshots" :key="snap.kind + ':' + snap.id" class="archive-row">
            <div class="archive-meta">
              <span class="snap-kind" :class="`snap-kind-${snap.kind}`">
                {{ snap.kind === 'published' ? 'Published' : 'Spread switch' }}
              </span>
              <span class="archive-name">{{ snap.spread || snap.label }}</span>
              <span class="archive-stamp">{{ formatTimestamp(snap.at) }}</span>
              <span v-if="snap.price != null" class="archive-price">{{ snap.price }} <CaratMark :size="13" /></span>
            </div>
            <button type="button" class="archive-restore" :disabled="restoringSnapshot" @click="restoreSnapshot(snap)">Restore</button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Per-beat history modal — opened by a beat card's ↻ icon; lists that
         beat's prior approved prompts, scoped to the selected beat. -->
    <div v-if="beatHistoryBeat" class="archive-modal-overlay" @click.self="beatHistoryBeat = null">
      <div class="archive-modal" role="dialog" :aria-label="`${beatHistoryBeat.label} history`">
        <header class="archive-modal-head">
          <h4 class="archive-title">{{ beatHistoryBeat.label }} - History</h4>
          <button type="button" class="archive-modal-close" aria-label="Close" @click="beatHistoryBeat = null">×</button>
        </header>
        <p class="archive-sub">Prior approved prompts for this beat - load one back into your draft.</p>
        <div v-if="beatHistoryLoading" class="archive-empty">Loading…</div>
        <div v-else-if="!beatHistory.length" class="archive-empty">
          No saved versions yet. Each time you save this beat, the prompt is kept here.
        </div>
        <ul v-else class="archive-list">
          <li v-for="ver in beatHistory" :key="ver.id" class="archive-row beat-history-row">
            <div class="beat-history-meta">
              <span class="archive-stamp">{{ formatTimestamp(ver.saved_at) }}</span>
              <p class="beat-history-text">{{ ver.text || '(empty)' }}</p>
            </div>
            <button type="button" class="archive-restore" :disabled="restoringBeat" @click="restoreBeat(ver)">Load</button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Switch-confirmation modal — fires when the Mystic picks a different
         spread in the game-bar picker. -->
    <VariantSwitchModal
      v-if="pendingSwitch"
      mode="switch"
      :from-name="pendingSwitch.fromName"
      :to-name="pendingSwitch.toName"
      :busy="variantSwitching"
      @cancel="pendingSwitch = null"
      @confirm="confirmVariantSwitch"
    />

    <!-- Restore-confirmation modal — fires from the Archive modal. -->
    <VariantSwitchModal
      v-if="pendingRestore"
      mode="restore"
      :from-name="pendingRestore.fromName"
      :to-name="pendingRestore.toName"
      :archived-timestamp="formatTimestamp(pendingRestore.archive.archived_at)"
      :busy="variantSwitching"
      @cancel="pendingRestore = null"
      @confirm="confirmRestore"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import CaratMark from '@/components/ui/CaratMark.vue'
import {
  listReadingSessionSkills,
  getReadingSessionTree,
  updateReadingSessionVariant,
  updateVariantKey,
  restoreVariantArchive,
  updateReadingMoment,
  updateReadingMomentLimit,
  updateInterviewPace,
  updateMeaningSource,
  updatePositionLabel,
  updatePositionStream,
  listSnapshots,
  restoreGameSnapshot,
  listBeatHistory,
  restoreBeatVersion,
} from '@/api-service'
import { parseAxiosError } from '@/utils/general-utils'
import { dispatchFieldContext, studioAssistantState } from '@/utils/studio-assistant'
import SkillsEditorView from '../skills/editor/SkillsEditorView.vue'
import SkillSettingsInline from '../skills/editor/SkillSettingsInline.vue'
import VariantSwitchModal from './VariantSwitchModal.vue'
import BeatPreviewRail from './BeatPreviewRail.vue'
import ReadingModal from './ReadingModal.vue'
import PublishModal from './PublishModal.vue'
import FieldHelpIcon from '../FieldHelpIcon.vue'

const skills = ref([])
const activeSkill = ref(null)
const activeSub = ref('card_deck')
const tree = ref(null)
const loadingTree = ref(false)
const error = ref('')
const savingVariantId = ref(null)
const savedVariantId = ref(null)

const activeVariantId = ref(null)
const priceError = ref('')

const pendingSwitch = ref(null)
const pendingRestore = ref(null)
const variantSwitching = ref(false)

// ── Draft → Publish gate ──
const hasUnpublishedChanges = ref(false)
// Snapshots — one timeline: published versions + spread-switch backups.
const showSnapshotsModal = ref(false)
const snapshots = ref([])
const snapshotsLoading = ref(false)
const restoringSnapshot = ref(false)
// Per-beat prompt history
const beatHistoryBeat = ref(null)
const beatHistory = ref([])
const beatHistoryLoading = ref(false)
const restoringBeat = ref(false)

const DEFAULT_PRICE_FLOOR = 25

// Journey sub-tabs, in seeker order.
const subTabs = [
  { key: 'card_deck',      label: 'Card Deck' },
  { key: 'start',          label: 'Start' },
  { key: 'card_selection', label: 'Card Selection' },
  { key: 'reading',        label: 'Reading' },
]

// ── Beat definitions (plain-language $moment_intent per journey moment) ──
const START_BEATS = [
  { slug: 'grounding', label: 'Grounding',
    hint: 'The first thing seekers hear. Help them feel comfortable, and tell them what comes next.',
    placeholder: "e.g., There's no hurry here. In a moment, I'll ask what's on your mind." },
  { slug: 'interview', label: 'Interview',
    hint: "Draw out the seeker's specific question.",
    placeholder: 'e.g., What decision is weighing on you right now?' },
]
const READING_BEATS = [
  { slug: 'reading', label: 'The Reading',
    note: '<b>The Reading</b> - the woven reading that ties all the cards into one answer.',
    hint: 'How you weave the cards into one summary (the stack analysis).',
    placeholder: 'e.g., Connect the cards into one throughline, then a single clear takeaway.' },
  { slug: 'image', label: 'Image Direction',
    note: "<b>Image Direction</b> - your creative brief for the reading’s illustration.",
    hint: "Your creative direction for the reading's illustration - riff on top of your visual brand.",
    placeholder: "e.g., Lean cinematic and hopeful - a single figure mid-stride, light breaking through.",
    showsBrand: true },
]
// Card-reveal beats are GAME-DERIVED — one per position of the active spread.
const ROLE_LABELS = { past: 'Past', present: 'Present', future: 'Future', advice: 'Advice', focus: 'Focus' }
const CARD_COUNT_ROLES = { 1: ['focus'], 3: ['past', 'present', 'future'], 4: ['past', 'present', 'future', 'advice'] }
// Dead-simple, position-specific examples for each Card Summary lean — so it's
// obvious what to write for THIS card. Falls back to a generic line.
const LEAN_SAMPLES = {
  past: 'e.g., Focus on the lesson this card carries forward.',
  present: 'e.g., Speak to what they’re facing right now.',
  future: 'e.g., Point to what’s coming - keep it hopeful.',
  advice: 'e.g., Give one clear, doable next step.',
  focus: 'e.g., Speak straight to the heart of their question.',
}

// ── The per-beat moment values, loaded from the tree ──
// ``style`` is the cross-cutting $reader_slant; ``moments`` are the per-beat
// $moment_intent texts; ``limits`` are per-beat response length caps (chars).
const readingStyle = reactive({ style: '', moments: {}, limits: {} })
const momentSaved = ref(null)

// Interview tempo — the single pacing dial (shapes how the interview opens AND
// when the cards are offered). Shown only on the Interview beat.
const PACE_OPTIONS = [
  { value: 'quick', label: 'Quick to the cards' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'patient', label: 'Let them breathe' },
]
const interviewPace = ref('balanced')
const meaningSource = ref('symbolic')
// Tap-toggle for the meaning-source info popover (hover handles desktop).
const meaningInfoOpen = ref(false)
// The reader's persistent visual brand (always-applied), shown read-only on the
// Image Direction beat so the Mystic sees what their creative direction rides on.
const brandReference = ref('')
// Per-position label overrides (role → label) for EVERY position of the active
// spread — the Mystic's rename of "Past"/"Focus"/etc., injected into the reveal
// pre-line. Blank reverts to the spread's own label.
const positionLabels = reactive({})
// role → bool: whether this card position echoes into the chat window when
// drawn / replayed. Unset means default (every position shows EXCEPT advice).
const positionStreamConfig = reactive({})
const paceIndex = computed(() => {
  const i = PACE_OPTIONS.findIndex(o => o.value === interviewPace.value)
  return i < 0 ? 1 : i
})
function onPaceSlide(idx) {
  const opt = PACE_OPTIONS[Number(idx)]
  if (opt) setInterviewPace(opt.value)
}
async function setInterviewPace(value) {
  if (!activeSkill.value || interviewPace.value === value) return
  const prev = interviewPace.value
  interviewPace.value = value
  try {
    await updateInterviewPace(activeSkill.value, value)
  } catch (e) {
    interviewPace.value = prev
    error.value = parseAxiosError(e)
  }
}

// ── Simulator: which beat (if any) the right rail is previewing ────
// Null = simulator hidden. Set by the per-beat "Simulate" button; the rail
// appears (and auto-runs) for that beat until dismissed.
const simBeat = ref(null)
// Bumped on every Simulate / Re-Simulate click so the rail re-runs even when the
// beat hasn't changed.
const simRunNonce = ref(0)
const showSim = computed(() => !!simBeat.value)
// Card Selection stacks the simulator BELOW the beats (full-width chat-window
// preview); Start/Reading keep it as a right rail.
const simStacked = computed(() => activeSub.value === 'card_selection')
const simBeatIntent = computed(() =>
  simBeat.value ? (readingStyle.moments[simBeat.value.slug] || '') : '')
const simBeatLimit = computed(() =>
  simBeat.value ? (readingStyle.limits[simBeat.value.slug] || 0) : 0)
// Shared simulator props — bound to whichever layout (rail vs pinned dock) is
// active so the two BeatPreviewRail usages never drift.
const simRailProps = computed(() => ({
  skillKey: activeSkill.value,
  beat: simBeat.value,
  momentIntent: simBeatIntent.value,
  readerSlant: readingStyle.style,
  maxChars: simBeatLimit.value,
  meaningSource: meaningSource.value,
  runNonce: simRunNonce.value,
}))

// "Refine / Write with Mystic Aida" → open the Mystic Aida assistant scoped to
// this beat (a 'click'-sourced field context force-opens the shared studio Aida
// column). The assistant carries the Magic/Spoken toggle and writes its result
// back into this card via ``studio-assistant:polish-applied``.
function openBeat(m) {
  dispatchFieldContext({
    field: `moment:${m.slug}`, label: m.label, panel: 'reading-session',
    value: readingStyle.moments[m.slug] || '', source: 'click',
  })
}

// "Simulate" / "Re-Simulate" → open the simulator rail for this beat and run it.
// Re-clicking the same beat (now labelled "Re-Simulate") re-runs; the rail's ×
// closes it.
function simulateBeat(m) {
  // THE READING opens the full-reading MODAL (the rail can't hold a real
  // reading); other beats use the rail.
  if (m.slug === 'reading') {
    simulateReading()
    return
  }
  simBeat.value = { slug: m.slug, label: m.label }
  simRunNonce.value++
}
// The Reading tab's single Simulate — opens the modal (reading + illustration).
function simulateReading() {
  showReadingModal.value = true
  readingModalNonce.value++
}
function closeSim() {
  simBeat.value = null
}
// The full-reading modal (THE READING simulator).
const showReadingModal = ref(false)
const readingModalNonce = ref(0)
// The Publish modal (review diff → preview → publish).
const showPublishModal = ref(false)
// Switching journey tabs dismisses any open simulator — it belongs to the beat
// you were on, not the one you're moving to.
watch(activeSub, () => { simBeat.value = null; showReadingModal.value = false })

// A beat card is "active" (highlighted) when the assistant is open and scoped to
// it — drives the active border.
function beatIsOpen(slug) {
  return studioAssistantState.open
    && studioAssistantState.activeField?.field === `moment:${slug}`
}

// ── Collapsible cards (accordion): when the active tab has more than one beat,
// cards start collapsed (header only) and only ONE can be open at a time. A
// lone beat stays open. ──
const expandedSlug = ref(null)
const activeBeatsCount = computed(() => {
  if (activeSub.value === 'start') return START_BEATS.length
  if (activeSub.value === 'card_selection') return cardRevealBeats.value.length
  if (activeSub.value === 'reading') return READING_BEATS.length + readingSummaryBeats.value.length
  return 0
})
function isExpanded(slug) {
  if (activeBeatsCount.value <= 1) return true
  return expandedSlug.value === slug
}
function toggleBeat(m) {
  if (activeBeatsCount.value <= 1) return
  // Accordion: clicking the open card closes it; clicking another opens it and
  // closes whatever was open.
  const opening = expandedSlug.value !== m.slug
  expandedSlug.value = opening ? m.slug : null
  // The simulator stays open until its × — but if it's open and we just opened
  // a (different) beat, re-scope it to that beat and re-run the simulation.
  if (opening && simBeat.value) {
    simBeat.value = { slug: m.slug, label: m.label }
    simRunNonce.value++
  }
  // Keep the Mystic Aida assistant in sync: if it's open, opening a beat
  // re-scopes it to that beat so "Helping with …" follows the open card (same
  // path as clicking that beat's Refine).
  if (opening && studioAssistantState.open) {
    dispatchFieldContext({
      field: `moment:${m.slug}`, label: m.label, panel: 'reading-session',
      value: readingStyle.moments[m.slug] || '', source: 'click',
    })
  }
}

// Aida finished shaping a beat → write the polished text into the prompt + save.
// Fires via the shared ``studio-assistant:polish-applied`` window event, the
// same channel the other assisted panels use (auto-write fields).
function onPolishApplied(e) {
  const { field, value } = e.detail || {}
  if (!field || value === undefined) return
  // Position-name fields (e.g. the 1-card Focus) — Aida suggests a name; write
  // it through the same path as the inline rename.
  if (field.startsWith('position:')) {
    const role = field.slice('position:'.length)
    if (!role) return
    const next = (value || '').trim()
    if (next) positionLabels[role] = next
    else delete positionLabels[role]
    if (!activeSkill.value) return
    updatePositionLabel(activeSkill.value, role, next)
      .then(() => { hasUnpublishedChanges.value = true })
      .catch(err => { error.value = parseAxiosError(err) })
    return
  }
  if (!field.startsWith('moment:')) return
  const slug = field.slice('moment:'.length)
  if (!slug) return
  readingStyle.moments[slug] = value
  saveMoment(slug)
}

const activeVariant = computed(() => {
  if (!tree.value || !tree.value.variants) return null
  return tree.value.variants.find(v => v.id === activeVariantId.value) || null
})

const currentFloor = computed(() => {
  const v = activeVariant.value
  if (!v) return DEFAULT_PRICE_FLOOR
  return Number.isFinite(v.min_price_carats) ? v.min_price_carats : DEFAULT_PRICE_FLOOR
})

const activeVariantWithSkill = computed(() => {
  if (!activeVariant.value) return null
  return { ...activeVariant.value, skill_key: tree.value?.skill_key }
})

// Positions of the active spread → the per-position Card Selection beats.
// Prefer the full descriptors from the tree ([{role, label}], data-driven from
// the spread itself); fall back to role keys, then to a card-count guess so the
// panel still renders if an older payload omits positions.
const spreadPositions = computed(() => {
  const v = activeVariant.value
  if (v?.positions?.length) return v.positions
  const roles = v?.roles?.length ? v.roles : (CARD_COUNT_ROLES[v?.card_count] || CARD_COUNT_ROLES[4])
  return roles.map(role => ({ role, label: '' }))
})
// Every position gets the full treatment now (1-card AND multi-card): an
// inline-editable label + the per-role AI name assistant. The label shown is
// the Mystic's override → the spread's own label → a sensible default.
const cardRevealBeats = computed(() => spreadPositions.value.map(({ role, label: spreadLabel }) => {
  const label = (positionLabels[role] || '').trim()
    || (spreadLabel || '').trim()
    || ROLE_LABELS[role] || humanizeKey(role)
  return {
    slug: `card_reveal_${role}`,
    role,
    label,
    editableLabel: true,
    hint: `In ~5 words: what the ${label} card says for this seeker.`,
    placeholder: `e.g., a few evocative words for the ${label} position.`,
  }
}))

// Per-card SUMMARY leans for the Reading tab — one per spread position, hidden
// for a 1-card draw (its single card is covered by THE READING). Each steers
// card_summary_<role> (the fuller per-card read in the reading window).
const readingSummaryBeats = computed(() => {
  if (spreadPositions.value.length <= 1) return []
  return spreadPositions.value.map(({ role, label: spreadLabel }) => {
    const label = (positionLabels[role] || '').trim()
      || (spreadLabel || '').trim() || ROLE_LABELS[role] || humanizeKey(role)
    return {
      slug: `card_summary_${role}`,
      role,
      label,
      placeholder: LEAN_SAMPLES[role] || `e.g., What the ${label} card should get across.`,
    }
  })
})

// Inline-rename any card position. Persists as a draft override keyed by role;
// blank reverts to the spread's default label.
async function savePositionLabel(role, ev) {
  if (!activeSkill.value) return
  const next = (ev?.target?.value || '').trim()
  const current = (positionLabels[role] || '').trim()
  if (next === current) return
  if (next) positionLabels[role] = next
  else delete positionLabels[role]
  try {
    await updatePositionLabel(activeSkill.value, role, next)
    hasUnpublishedChanges.value = true
  } catch (e) {
    error.value = parseAxiosError(e)
  }
}

// Where a position's reveal lands in the chat — on_draw / after_summary /
// private. Mirrors card_meaning.position_stream_mode, including its defaults
// (advice is the closing counsel, so it lands after the weave) and its
// tolerance for the booleans this field used to hold.
function getStreamMode(role) {
  const fallback = role === 'advice' ? 'after_summary' : 'on_draw'
  if (!(role in positionStreamConfig)) return fallback
  const v = positionStreamConfig[role]
  if (typeof v === 'boolean') return v ? 'on_draw' : 'private'
  return ['on_draw', 'after_summary', 'private'].includes(v) ? v : fallback
}

// Set a position's reveal mode. Draft override; goes live on Publish.
async function saveStreamMode(role, mode) {
  if (!activeSkill.value) return
  const prev = positionStreamConfig[role]
  positionStreamConfig[role] = mode
  try {
    await updatePositionStream(activeSkill.value, role, mode)
    hasUnpublishedChanges.value = true
  } catch (e) {
    error.value = parseAxiosError(e)
    if (prev === undefined) delete positionStreamConfig[role]
    else positionStreamConfig[role] = prev
  }
}

// ── Variant switch flow (confirm → archive → API → re-seed) ─────
function onPickRequested({ key, variant }) {
  if (!key || key === variant.variant_key) return
  pendingSwitch.value = {
    fromName: humanizeKey(variant.spread_display_name || variant.variant_key),
    toName: humanizeKey(key),
    fromVariantKey: variant.variant_key,
    toVariantKey: key,
    variantId: variant.id,
  }
}

async function confirmVariantSwitch() {
  if (!pendingSwitch.value || !activeVariant.value) return
  const { toVariantKey, variantId } = pendingSwitch.value
  variantSwitching.value = true
  try {
    const refreshed = await updateVariantKey(variantId, toVariantKey)
    applyRefreshedVariant(refreshed)
    if (activeSkill.value) {
      const tree2 = await getReadingSessionTree(activeSkill.value)
      if (tree2) { tree.value = tree2; syncMomentsFromTree() }
    }
    priceError.value = ''
    pendingSwitch.value = null
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    variantSwitching.value = false
  }
}

// ── Restore flow ────────────────────────────────────────────────
function openRestoreModal(archive) {
  if (!activeVariant.value || !archive) return
  showSnapshotsModal.value = false
  const snap = archive.snapshot || {}
  pendingRestore.value = {
    fromName: humanizeKey(activeVariant.value.spread_display_name || activeVariant.value.variant_key),
    toName: humanizeKey(snap.spread_display_name || snap.variant_key),
    archive,
  }
}

async function confirmRestore() {
  if (!pendingRestore.value || !activeVariant.value) return
  const { archive } = pendingRestore.value
  variantSwitching.value = true
  try {
    const refreshed = await restoreVariantArchive(archive.id)
    applyRefreshedVariant(refreshed)
    if (activeSkill.value) {
      const tree2 = await getReadingSessionTree(activeSkill.value)
      if (tree2) { tree.value = tree2; syncMomentsFromTree() }
    }
    priceError.value = ''
    pendingRestore.value = null
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    variantSwitching.value = false
  }
}

function applyRefreshedVariant(refreshed) {
  if (!refreshed || !tree.value?.variants) return
  const idx = tree.value.variants.findIndex(v => v.id === refreshed.id)
  if (idx >= 0) tree.value.variants[idx] = refreshed
  activeVariantId.value = refreshed.id
}

function humanizeKey(s) {
  if (!s) return ''
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function formatTimestamp(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function syncMomentsFromTree() {
  const rs = (tree.value && tree.value.reading_style) || {}
  readingStyle.style = rs.style || ''
  readingStyle.moments = { ...(rs.moments || {}) }
  readingStyle.limits = { ...(rs.limits || {}) }
  interviewPace.value = (tree.value && tree.value.interview_pace) || 'balanced'
  meaningSource.value = rs.meaning_source || 'symbolic'
  Object.keys(positionLabels).forEach(k => delete positionLabels[k])
  Object.assign(positionLabels, rs.position_labels || {})
  Object.keys(positionStreamConfig).forEach(k => delete positionStreamConfig[k])
  Object.assign(positionStreamConfig, rs.position_stream_config || {})
  brandReference.value = (tree.value && tree.value.brand_reference) || ''
  hasUnpublishedChanges.value = !!(tree.value && tree.value.has_unpublished_changes)
}

// Word-counted beats and their ranges (mirror the backend _word_range): a card
// turn is a few words, a reading is a short paragraph, the image direction is a
// roomy brief. Grounding/Interview stay character-counted.
const WORD_RANGES = {
  reading: { min: 25, max: 150, step: 5 },
  image: { min: 50, max: 500, step: 25 },
  card_reveal: { min: 1, max: 10, step: 1 },
}
function isWordBeat(slug) {
  return slug === 'card_reveal' || slug.startsWith('card_reveal_')
    || slug === 'reading' || slug === 'image'
}
function wordRange(slug) {
  if (slug === 'reading') return WORD_RANGES.reading
  if (slug === 'image') return WORD_RANGES.image
  return WORD_RANGES.card_reveal
}
function beatLimitMin(slug) { return isWordBeat(slug) ? wordRange(slug).min : 50 }
function beatLimitMax(slug) { return isWordBeat(slug) ? wordRange(slug).max : 255 }
function beatLimitStep(slug) { return isWordBeat(slug) ? wordRange(slug).step : 5 }

async function setMeaningSource(source) {
  if (!activeSkill.value || meaningSource.value === source) return
  const prev = meaningSource.value
  meaningSource.value = source
  try {
    await updateMeaningSource(activeSkill.value, source)
    hasUnpublishedChanges.value = true
  } catch (e) {
    meaningSource.value = prev
    error.value = parseAxiosError(e)
  }
}

async function selectSkill(key) {
  activeSkill.value = key
  loadingTree.value = true
  tree.value = null
  activeVariantId.value = null
  priceError.value = ''
  error.value = ''
  try {
    tree.value = await getReadingSessionTree(key)
    syncMomentsFromTree()
    if (tree.value && tree.value.variants && tree.value.variants.length) {
      activeVariantId.value = tree.value.variants[0].id
    }
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    loadingTree.value = false
  }
}

async function savePricing(variant) {
  // Price edits are DRAFT — bound to price_carats_draft; Publish Live makes
  // them the live charge.
  const value = variant.price_carats_draft
  if (!Number.isFinite(value)) {
    priceError.value = 'Enter a number.'
    return
  }
  const floor = currentFloor.value
  if (value < floor) {
    priceError.value = `Minimum charge for ${humanizeKey(variant.spread_display_name || variant.variant_key)} is ${floor} carats.`
    return
  }
  priceError.value = ''
  savingVariantId.value = variant.id
  savedVariantId.value = null
  try {
    const data = await updateReadingSessionVariant(variant.id, { price_carats: variant.price_carats_draft })
    if (data.price_carats_draft != null) variant.price_carats_draft = data.price_carats_draft
    hasUnpublishedChanges.value = true
    savedVariantId.value = variant.id
    setTimeout(() => { if (savedVariantId.value === variant.id) savedVariantId.value = null }, 2000)
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    savingVariantId.value = null
  }
}

async function saveMoment(slug) {
  if (!activeSkill.value) return
  try {
    await updateReadingMoment(activeSkill.value, slug, readingStyle.moments[slug] || '')
    hasUnpublishedChanges.value = true
    momentSaved.value = slug
    setTimeout(() => { if (momentSaved.value === slug) momentSaved.value = null }, 1500)
  } catch (e) {
    error.value = parseAxiosError(e)
  }
}

// The Reading's Title direction (moments.reading_title) — typed inline, saved
// like any moment. (The ✨ assistant writes it via the same polish path.)
function saveTitleDirection(ev) {
  const next = (ev?.target?.value || '').trim()
  if ((readingStyle.moments.reading_title || '') === next) return
  readingStyle.moments.reading_title = next
  saveMoment('reading_title')
}


// ── Snapshots ── (one timeline; Publish itself lives in PublishModal.)
async function openSnapshots() {
  showSnapshotsModal.value = true
  if (!activeSkill.value) return
  snapshotsLoading.value = true
  try {
    snapshots.value = await listSnapshots(activeSkill.value)
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    snapshotsLoading.value = false
  }
}

async function restoreSnapshot(snap) {
  if (restoringSnapshot.value) return
  // Spread-switch backups change the active spread — route through the confirm
  // modal. Published versions just load back into the draft.
  if (snap.kind === 'switch') {
    openRestoreModal({ id: snap.id, snapshot: { spread_display_name: snap.spread } })
    return
  }
  if (!activeSkill.value) return
  restoringSnapshot.value = true
  try {
    await restoreGameSnapshot(activeSkill.value, snap.id)
    showSnapshotsModal.value = false
    const tree2 = await getReadingSessionTree(activeSkill.value)
    if (tree2) { tree.value = tree2; syncMomentsFromTree() }
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    restoringSnapshot.value = false
  }
}

async function openBeatHistory(beat) {
  beatHistoryBeat.value = beat
  beatHistory.value = []
  if (!activeSkill.value) return
  beatHistoryLoading.value = true
  try {
    beatHistory.value = await listBeatHistory(activeSkill.value, beat.slug)
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    beatHistoryLoading.value = false
  }
}

async function restoreBeat(ver) {
  const beat = beatHistoryBeat.value
  if (!activeSkill.value || !beat || restoringBeat.value) return
  restoringBeat.value = true
  try {
    const res = await restoreBeatVersion(activeSkill.value, beat.slug, ver.id)
    if (res && res.reading_style) {
      readingStyle.moments = { ...(res.reading_style.moments || {}) }
      readingStyle.limits = { ...(res.reading_style.limits || {}) }
    }
    hasUnpublishedChanges.value = true
    beatHistoryBeat.value = null
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    restoringBeat.value = false
  }
}

// Persist a beat's response length cap (chars). Blur-save like the moments; the
// server clamps to a sane range and echoes back the normalized value.
async function saveLimit(slug) {
  if (!activeSkill.value) return
  const val = parseInt(readingStyle.limits[slug], 10)
  if (!Number.isFinite(val)) return
  try {
    const res = await updateReadingMomentLimit(activeSkill.value, slug, val)
    if (res && res.reading_style && res.reading_style.limits) {
      readingStyle.limits = { ...res.reading_style.limits }
    }
    hasUnpublishedChanges.value = true
  } catch (e) {
    error.value = parseAxiosError(e)
  }
}

onMounted(async () => {
  try {
    const data = await listReadingSessionSkills()
    skills.value = data.skills || []
    if (skills.value.length > 0) {
      await selectSkill(skills.value[0].skill_key)
    }
  } catch (e) {
    error.value = parseAxiosError(e)
  }
  window.addEventListener('studio-assistant:polish-applied', onPolishApplied)
})
onBeforeUnmount(() => {
  window.removeEventListener('studio-assistant:polish-applied', onPolishApplied)
})
</script>

<style scoped>
@import '@/styles/studio-forms.css';

/* Opt out of the shared 640px ``.studio-panel`` cap — this surface is a
   two-column layout (prompt + simulator) that needs the full section canvas,
   not the single-column form width. The double-class selector guarantees we
   beat the global cap regardless of import scoping. The canvas itself stays the
   same width as every other section (``.reading-page`` 1024). */
.rsp-wide.studio-panel {
  max-width: none;
}

.skill-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid rgba(195, 169, 255, 0.18);
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.skill-tab {
  padding: 10px 18px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(212, 196, 232, 0.6);
  font-family: 'Cinzel', serif;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.skill-tab:hover { color: rgba(232, 216, 255, 0.9); }
.skill-tab.active { color: #C9A94E; border-bottom-color: #C9A94E; }
.skill-tab.add-skill { color: rgba(195, 169, 255, 0.35); font-style: italic; }
.skill-tab.add-skill:disabled { cursor: not-allowed; }

/* ── Game bar: variant picker + price + archive (above the sub-tabs) ── */
.game-bar {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 14px 16px;
  margin-bottom: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  flex-wrap: wrap;
}
.game-bar > :deep(.skill-settings) { margin-right: auto; }
.game-bar-right { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }

/* ── Phone (≤560px): fixed-width Studio columns would overflow a ~390px screen.
   Collapse the fixed panes to full-width and stack the two-column splits + the
   game bar. Desktop layout above is untouched. ── */
@media (max-width: 560px) {
  .sub-center,
  .cs-pane { width: 100%; max-width: 100%; }
  .sub-body-split { grid-template-columns: 1fr; }
  .game-bar { flex-direction: column; align-items: stretch; }
  .game-bar-right { flex-direction: column; align-items: stretch; }
  .price-input { width: 100%; }
  .variant-pricing .price-input { width: auto; flex: 1 1 auto; min-width: 0; }
  .publish-wrap { align-items: stretch; }
}
.variant-pricing { display: flex; align-items: center; gap: 6px; }
.price-label { margin-right: 4px; font-size: 13px; color: rgba(212, 196, 232, 0.8); white-space: nowrap; }
.publish-wrap { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.publish-hint { font-size: 11.5px; line-height: 1.3; color: rgba(212, 196, 232, 0.7); text-align: center; }
.price-input {
  width: 86px;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: #E8D8FF;
  font-family: 'Inter', sans-serif;
  text-align: right;
}
.price-input-error { border-color: rgba(244, 67, 54, 0.7); box-shadow: 0 0 0 1px rgba(244, 67, 54, 0.35); }
.emerald-symbol { color: #50C7A8; font-size: 16px; }
.price-status { font-size: 11px; color: rgba(195, 169, 255, 0.6); margin-left: 4px; }
.price-status.saved { color: #50C7A8; }
.price-error { margin: 4px 0 12px; font-size: 12px; color: rgba(244, 67, 54, 0.95); }
.archive-link {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.9);
  padding: 6px 14px;
  border-radius: 999px;
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.18s;
}
.archive-link:hover { border-color: #C9A94E; color: #C9A94E; }

/* Publish Live — the draft→live gate trigger. Highlighted while there are
   unpublished changes; muted when draft matches live. */
.publish-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.55);
  padding: 6px 16px;
  border-radius: 999px;
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.18s;
}
.publish-btn:disabled { cursor: default; }
.publish-btn.has-changes {
  border-color: #C9A94E;
  color: #1a1030;
  background: #C9A94E;
  cursor: pointer;
}
.publish-btn.has-changes:hover { background: #d8bb63; }
.publish-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #1a1030;
  box-shadow: 0 0 0 2px rgba(26, 16, 48, 0.25);
}

/* Per-beat ↻ history icon. */
.beat-btn-history {
  padding: 4px 8px;
  font-size: 13px;
  line-height: 1;
}

/* Beat-history modal rows show the prompt text. */
.beat-history-row { align-items: flex-start; }
.beat-history-meta { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.beat-history-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(232, 216, 255, 0.85);
  white-space: pre-wrap;
}

/* ── Sub-tabs ── */
.sub-tabs {
  display: flex;
  gap: 2px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
}
.sub-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: none;
  color: rgba(195, 169, 255, 0.55);
  font-size: 12px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}
.sub-tab:hover { color: rgba(232, 216, 255, 0.9); }
.sub-tab.active { color: #E8D8FF; border-bottom-color: rgba(201, 169, 78, 0.7); }
.sub-tab-spinner {
  width: 10px; height: 10px; border-radius: 50%;
  border: 2px solid rgba(201, 169, 78, 0.25); border-top-color: #C9A94E;
  animation: rsp-spin 0.8s linear infinite;
}
@keyframes rsp-spin { to { transform: rotate(360deg); } }

.sub-intro {
  margin: 0 0 16px;
  font-size: 13px;
  color: rgba(195, 169, 255, 0.6);
  font-style: italic;
  line-height: 1.5;
}

/* Card meaning source toggle. */
.meaning-source {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 0 0 16px;
}
.meaning-source-toggle {
  display: inline-flex;
  border: 1px solid rgba(195, 169, 255, 0.28);
  border-radius: 999px;
  overflow: hidden;
}
.ms-opt {
  background: transparent;
  border: none;
  color: rgba(212, 196, 232, 0.7);
  padding: 5px 14px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}
.ms-opt.active { background: rgba(201, 169, 78, 0.18); color: #C9A94E; }
.ms-opt:hover:not(.active) { color: rgba(232, 216, 255, 0.95); }

/* Info dot — explains both meaning sources on hover / tap. */
.meaning-source-info { position: relative; display: inline-flex; }
.ms-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  background: rgba(201, 169, 78, 0.12);
  border: 1px solid rgba(201, 169, 78, 0.4);
  border-radius: 50%;
  color: #C9A94E;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 12px;
  font-style: italic;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s;
}
.ms-info-btn:hover { background: rgba(201, 169, 78, 0.22); border-color: #C9A94E; }
.ms-info-pop {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  width: 280px;
  max-width: 70vw;
  display: none;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: #160c2b;
  border: 1px solid rgba(201, 169, 78, 0.35);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  font-size: 12px;
  line-height: 1.45;
  color: rgba(232, 216, 255, 0.85);
  font-style: normal;
}
.meaning-source-info:hover .ms-info-pop,
.meaning-source-info.open .ms-info-pop { display: flex; }
.ms-info-row b { color: #C9A94E; font-weight: 600; }

.deck-picker-section { margin-top: 4px; }

/* ── Beat fields (plain-language $moment_intent authoring) ── */
.beat-field { margin-bottom: 14px; }
/* Clickable card header: title (left) + char cap (right). Toggles the body. */
.beat-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
}
.beat-head-left { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
.beat-chevron {
  color: rgba(201, 169, 78, 0.7);
  font-size: 12px;
  line-height: 1;
  transition: transform 0.15s;
  display: inline-block;
}
.beat-chevron.open { transform: rotate(90deg); }
.beat-label-text {
  font-family: 'Cinzel', serif;
  font-size: 18px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #C9A94E;
  font-weight: 600;
}
/* Inline-renamable position label (1-card Focus) — reads as the title until
   focused, then a subtle editable field. */
.beat-label-edit {
  font-family: 'Cinzel', serif;
  font-size: 18px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #C9A94E;
  font-weight: 600;
  background: transparent;
  border: none;
  border-bottom: 1px dashed rgba(201, 169, 78, 0.35);
  padding: 1px 2px;
  max-width: 220px;
  cursor: text;
}
.beat-label-edit:hover { border-bottom-color: rgba(201, 169, 78, 0.6); }
.beat-label-edit:focus {
  outline: none;
  border-bottom-color: #C9A94E;
  background: rgba(201, 169, 78, 0.06);
}
.beat-label-help { display: inline-flex; align-items: center; margin-left: 6px; }
.beat-card-body { padding: 0 16px 14px; }
/* Reading tab: one Simulate above both beats (reading + illustration). */
.reading-sim-bar { display: flex; justify-content: flex-end; margin-bottom: 14px; }
.reading-sim-btn {
  background: rgba(102, 51, 153, 0.45);
  border: 1px solid rgba(195, 169, 255, 0.45);
  color: #E8D8FF;
  border-radius: 999px;
  padding: 7px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.reading-sim-btn:hover { background: rgba(102, 51, 153, 0.6); border-color: #C9A94E; }
/* THE READING: the summary-title direction (a light knob). */
.reading-title-field { margin: 8px 0 4px; }
.reading-title-label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(195, 169, 255, 0.7);
  margin-bottom: 4px;
}
.reading-title-input {
  width: 100%;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(195, 169, 255, 0.25);
  border-radius: 8px;
  color: #E8D8FF;
  font-size: 13px;
  padding: 6px 10px;
  outline: none;
  box-sizing: border-box;
}
.reading-title-input:focus { border-color: #C9A94E; }
.reading-title-input::placeholder { color: rgba(195, 169, 255, 0.4); }
/* Image Direction: read-only reference to the persistent visual brand. */
.brand-ref {
  margin: 6px 0 10px;
  padding: 8px 10px;
  border-left: 2px solid rgba(201, 169, 78, 0.4);
  background: rgba(201, 169, 78, 0.05);
  border-radius: 0 6px 6px 0;
}
.brand-ref-label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(201, 169, 78, 0.75);
  margin-bottom: 3px;
}
.brand-ref-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(232, 216, 255, 0.7);
}
.brand-ref-empty {
  margin: 0;
  font-size: 12px;
  font-style: italic;
  color: rgba(195, 169, 255, 0.5);
}
/* Image Direction: brand collapsed to a one-liner. */
.brand-ref-mini { margin: 6px 0 8px; }
.brand-ref-mini > summary {
  list-style: none;
  cursor: pointer;
  font-size: 11px;
  letter-spacing: 0.03em;
  color: rgba(201, 169, 78, 0.8);
}
.brand-ref-mini > summary::-webkit-details-marker { display: none; }
.brand-ref-mini[open] > summary { margin-bottom: 6px; }
.brand-ref-mini .brand-ref-text,
.brand-ref-mini .brand-ref-empty {
  padding: 8px 10px;
  border-left: 2px solid rgba(201, 169, 78, 0.4);
  background: rgba(201, 169, 78, 0.05);
  border-radius: 0 6px 6px 0;
}
/* Card Selection: a single right-aligned row — word-count select + Simulate. */
.cs-beat-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
.cs-words { display: inline-flex; align-items: center; gap: 6px; }
.cs-stream { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }
.cs-stream-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(195, 169, 255, 0.6);
}
.cs-words-select,
.cs-stream-select {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(201, 169, 78, 0.4);
  color: #C9A94E;
  border-radius: 8px;
  padding: 4px 10px;
  font-family: 'Cinzel', serif;
  font-size: 13px;
  cursor: pointer;
  outline: none;
}
.cs-words-select:hover,
.cs-words-select:focus,
.cs-stream-select:hover,
.cs-stream-select:focus { border-color: #C9A94E; }
.cs-words-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(195, 169, 255, 0.6);
}
/* Per-beat response length cap, right-aligned in the card header. */
.beat-limit {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  margin-bottom: 12px;
}
.beat-limit-input {
  width: 46px;
  padding: 3px 6px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 5px;
  color: #E8D8FF;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  text-align: center;
  outline: none;
}
/* Hide number spinners for a cleaner pill. */
.beat-limit-input::-webkit-outer-spin-button,
.beat-limit-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.beat-limit-input { -moz-appearance: textfield; appearance: textfield; }
.beat-limit-input:focus { border-color: rgba(201, 169, 78, 0.5); }
.beat-limit-suffix {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(195, 169, 255, 0.5);
}
/* Each beat is a card showing the current prompt, with two actions: "Refine /
   Write with Mystic Aida" (opens the assistant) and "Simulate" (reveals the
   simulator rail for this beat). */
.beat-card {
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: 'Inter', sans-serif;
}
.beat-card:hover { border-color: rgba(201, 169, 78, 0.3); }
.beat-card-active {
  border-color: rgba(201, 169, 78, 0.65);
  box-shadow: 0 0 0 1px rgba(201, 169, 78, 0.25);
}
.beat-card-text {
  display: block;
  color: #E8D8FF;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
}
.beat-card-empty {
  display: block;
  color: rgba(255, 255, 255, 0.35);
  font-style: italic;
  font-size: 14px;
  line-height: 1.6;
}
.beat-card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
}
.beat-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}
.beat-btn-refine {
  background: rgba(201, 169, 78, 0.14);
  border-color: rgba(201, 169, 78, 0.4);
  color: #E8DCB4;
}
.beat-btn-refine:hover { background: rgba(201, 169, 78, 0.24); border-color: #C9A94E; }
.beat-btn-sim {
  margin-left: auto;
  background: rgba(102, 51, 153, 0.28);
  border-color: rgba(160, 120, 220, 0.5);
  color: #E8DDFF;
}
.beat-btn-sim:hover { background: rgba(102, 51, 153, 0.42); border-color: #b69be0; }
.beat-card-saved {
  font-size: 11px;
  color: #50C7A8;
}
/* Interview tempo dial — a slider across the degrees of pace, with the three
   stops labelled beneath and the selected one highlighted. Shown only on the
   Interview beat. */
.beat-pace { margin-bottom: 16px; max-width: 380px; }
.beat-pace-label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(195, 169, 255, 0.5);
  margin-bottom: 8px;
}
.beat-pace-slider {
  width: 100%;
  margin: 0;
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  outline: none;
  cursor: pointer;
}
.beat-pace-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #C9A94E;
  border: 2px solid #2a1d3a;
  cursor: pointer;
}
.beat-pace-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #C9A94E;
  border: 2px solid #2a1d3a;
  cursor: pointer;
}
.beat-pace-ticks {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 11px;
  font-family: 'Inter', sans-serif;
}
.beat-pace-tick {
  color: rgba(232, 216, 255, 0.45);
  transition: color 0.15s;
  flex: 0 0 auto;
}
.beat-pace-tick:first-child { text-align: left; }
.beat-pace-tick:last-child { text-align: right; }
.beat-pace-tick.active {
  color: #E8DCB4;
  font-weight: 600;
}

/* ── Beats (center) + simulator (right rail). The digital-assistance column is
   opt-in: it appears on the LEFT (the shared studio Aida panel) only when the
   operator clicks a beat's "Refine" button — hidden by default.
   Both columns are allowed to shrink (minmax floor of 0 on the center, a small
   floor on the rail) so the layout never overflows horizontally even when all
   three panels (assist · beats · simulator) are open and the main area is
   narrow. */
.sub-body-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 320px);
  gap: 1.25rem;
  align-items: start;
  padding-top: 8px;
}
/* Fixed beats-column width. Sized to fit alongside the simulator rail so the
   cards are the SAME width whether the rail is open or not — opening/closing it
   never resizes them. (640 was just wide enough to get squeezed a few px when
   the rail opened, which read as the cards "growing" once it closed.) */
.sub-center { min-width: 0; width: 600px; max-width: 100%; }
.sub-rail { position: sticky; top: 0; min-width: 0; }

/* Card Selection: a height-bounded pane so ONLY the beats list scrolls — the
   meaning toggle stays fixed up top and the chat-window preview stays pinned at
   the bottom (above the menu bar). The ~330px reserves the studio chrome above
   (tabs · game bar · skill tabs · sub-tabs) + the fixed bottom-nav; tune if the
   preview ever clips or floats. */
.sub-center.sub-center-cs { width: 100%; }
.cs-teaser-note {
  margin: 12px 0 4px;
  font-size: 12.5px;
  line-height: 1.5;
  color: rgba(212, 196, 232, 0.7);
}
.cs-teaser-note b { color: #C9A94E; font-weight: 600; }
/* Section descriptor above a beat group / beat in the Reading tab. */
.beat-note {
  margin: 0 0 8px;
  font-size: 12.5px;
  line-height: 1.5;
  color: rgba(212, 196, 232, 0.7);
}
/* :deep so the gold reaches the section name inside v-html notes too. */
.beat-note :deep(b) { color: #C9A94E; font-weight: 600; }
.beat-note-inline { margin-top: 16px; }   /* spacing above The Reading / Image Direction */
.cs-pane {
  display: flex;
  flex-direction: column;
  width: 600px;
  max-width: 100%;
  /* max-height (not height): when the content is short the pane shrinks so the
     preview sits right under the beats; when it's tall the beats scroll and the
     preview stays bottom-pinned — either way it never runs under the menu bar.
     The ~330 reserves the studio chrome above + the fixed bottom-nav. */
  max-height: calc(100dvh - 330px);
}
.cs-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 6px 4px 2px;
}
.cs-sim {
  flex: 0 0 auto;
  margin-top: 12px;
  width: 100%;
}
/* Collapse to a single column when the canvas is too tight: beats, then the
   simulator below. */
@media (max-width: 900px) {
  .sub-body-split { grid-template-columns: 1fr; }
  .sub-rail {
    position: static;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
}

/* ── Archive modal ── */
.archive-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(6, 0, 14, 0.66);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.archive-modal {
  width: 100%;
  max-width: 520px;
  background: #12081f;
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 30px 70px -30px #000;
}
.archive-modal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.archive-modal-close {
  background: transparent; border: none; cursor: pointer;
  color: rgba(212, 196, 232, 0.7); font-size: 22px; line-height: 1;
}
.archive-modal-close:hover { color: #C9A94E; }
.archive-title {
  margin: 0;
  font-family: 'Cinzel', serif;
  font-size: 14px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(232, 216, 255, 0.85);
}
.archive-sub { font-size: 12px; color: rgba(195, 169, 255, 0.55); font-style: italic; margin: 0 0 14px; }
.archive-empty {
  padding: 14px 16px;
  font-size: 13px;
  color: rgba(195, 169, 255, 0.55);
  font-style: italic;
  border: 1px dashed rgba(195, 169, 255, 0.18);
  border-radius: 10px;
}
.archive-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.archive-row {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
}
.archive-meta { display: flex; flex-wrap: wrap; align-items: baseline; gap: 12px; }
/* Snapshot kind tag — distinguishes a published version from a spread-switch backup. */
.snap-kind {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  padding: 2px 8px; border-radius: 999px; align-self: center;
}
.snap-kind-published { color: #C9A94E; background: rgba(201, 169, 78, 0.14); border: 1px solid rgba(201, 169, 78, 0.4); }
.snap-kind-switch { color: rgba(195, 169, 255, 0.85); background: rgba(102, 51, 153, 0.22); border: 1px solid rgba(195, 169, 255, 0.3); }
.archive-name { font-family: 'Cinzel', serif; font-size: 13px; color: #E8D8FF; }
.archive-stamp { font-size: 11px; color: rgba(195, 169, 255, 0.55); font-style: italic; }
.archive-price { font-size: 12px; color: #50C7A8; }
.archive-restore {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.9);
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-family: 'Cinzel', serif;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: all 0.18s;
}
.archive-restore:hover:not(:disabled) { border-color: #C9A94E; color: #C9A94E; }
.archive-restore:disabled { opacity: 0.5; cursor: wait; }

.empty-hint {
  padding: 18px;
  text-align: center;
  color: rgba(195, 169, 255, 0.45);
  font-style: italic;
  font-size: 13px;
  border: 1px dashed rgba(195, 169, 255, 0.18);
  border-radius: 10px;
}
.loading { padding: 18px; color: rgba(195, 169, 255, 0.6); font-style: italic; }
.error-message { color: rgba(244, 67, 54, 0.9); font-size: 13px; margin-top: 12px; }
</style>
